import React, { useLayoutEffect, useMemo, useRef, useState, useEffect } from "react";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import { List } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

import useSelectableCells from "../extensions/useSelectableCells";
import useFillDownCells from "../extensions/useFillDownCells";
import useNotification from "../../../hooks/notification";
import { useInfinityScroll } from "../../../utils/infinityScroll";
import TableRow, { isEqualRow } from "./TableRow";
import { nearestEditableCoords, parseCopyPaste, parseFillDown } from "../utils";
import { buildFillDownPayload } from "./utils";
import { callerField, fieldAttrName } from "../../../utils/caller";
import { MIN_HEIGHT } from "../TableHeader/index";
import {
  useNavigationEffect,
  useOnCopyPasteEffect,
  useOnEscEffect,
  useOnReFocuse,
  useOnShiftTabEffect,
  useOnTabEffect,
} from "../../../utils/keyEffects";

const TableRowMemo = React.memo(
  ({ style, index, ariaAttributes, ...props }) => <TableRow {...props} style={style} rowIndex={index} />,
  isEqualRow
);

const TableBody = ({
  size,
  totalCount,
  loading,
  fields,
  fieldsProps,
  entities,
  onLoadMore,
  onChangeEntity,
  rowsCount,
  height,
  innerRef,
  tableHeader,
  disabledScroll,
  fieldsWidth,
  onFillDown,
  onCopyPaste,
  getRowBackgroundColor,
}) => {
  const [noselect, setNoselect] = useState(false);
  const { notifyError } = useNotification();
  const defaultSelectedCoords = nearestEditableCoords(fields);
  const [endCoords, setEndCoords] = useState([]);
  const [copyDiapason, setCopyDiapason] = useState([]);
  const [cutMode, setCutMode] = useState(false);
  const [editCoords, setEditCoords] = useState([]);
  const prevEntitiesRef = useRef(entities);
  const currentRowHeight = Math.ceil((height - MIN_HEIGHT - 1) / rowsCount);
  const rowHeight = currentRowHeight > 60 ? currentRowHeight : 60;

  const {
    selectedCoords,
    setSelectedCoords,
    prevSelectedCell,
    prevSelectedRow,
    nextSelectedCell,
    nextSelectedRow,
  } = useSelectableCells(defaultSelectedCoords);

  const rootCoords = selectedCoords;

  const handleOnFillDown = fillDownDiapason => {
    const { variables, localStateVariables } = buildFillDownPayload(
      fillDownDiapason,
      entities,
      fields
    );
    onFillDown(variables, { localStateVariables });
  };

  const { fillDownElementProps, fillDownCellProps, fillDownMode, fillDownReset } = useFillDownCells(
    {
      onFillDown: handleOnFillDown,
      rootCoords,
      endCoords,
      setEndCoords,
    }
  );

  const isNotEditState = isEmpty(editCoords);

  const selectCopyDiapason = ({ cutMode = false } = {}) => {
    const finalDiapason = isEmpty(endCoords) ? rootCoords : endCoords;

    if (isNotEditState) {
      setCutMode(cutMode);
      setCopyDiapason([rootCoords, finalDiapason]);
      fillDownReset();
    } else {
      setCopyDiapason([]);
    }
  };

  const handlePaste = async () => {
    if (isEmpty(copyDiapason)) {
      return;
    }

    const isMulti =
      copyDiapason[0][0] !== copyDiapason[1][0] || copyDiapason[0][1] !== copyDiapason[1][1];

    const finalCoords = isEmpty(endCoords) ? rootCoords : endCoords;
    if (isEmpty(copyDiapason)) {
      const text = await navigator.clipboard.readText();
      const id = entities[rootCoords[0]].id;
      const pasteKey = fieldAttrName(fields[rootCoords[1]]);
      onCopyPaste({ [id]: { [pasteKey]: text } });
      return;
    }

    try {
      let pasteDiapason;
      if (isMulti) {
        const isVertical = copyDiapason[0][1] === copyDiapason[1][1];
        if (isVertical) {
          pasteDiapason = [rootCoords, [copyDiapason[1][0], finalCoords[1]]];
        } else {
          pasteDiapason = [rootCoords, finalCoords];
        }
      } else {
        pasteDiapason = [rootCoords, finalCoords];
      }
      const variables = parseCopyPaste({
        collection: entities,
        fields,
        copyDiapason,
        cutMode,
        pasteDiaposon: pasteDiapason,
      });
      onCopyPaste(variables);
      setCopyDiapason([]);
      setSelectedCoords(rootCoords);
      setEndCoords(finalCoords);
    } catch (error) {
      notifyError(error);
      setSelectedCoords([]);
      setEndCoords([]);
    }
  };

  const handleNextSelectedCell = () => {
    fillDownReset();
    nextSelectedCell(fields, selectedCoords[0], selectedCoords[1]);
    const activeFields = fields.filter(fieldItem => !fieldItem.inactive && fieldItem.visible);
    const nextEditableIndex = fields.findIndex(
      (fieldItem, index) => index > selectedCoords[1] && !fieldItem.inactive && fieldItem.visible
    );

    const currentEntity = entities[selectedCoords[0]];
    const nextEntity = entities[selectedCoords[0] + 1];

    let dataField;
    if (nextEditableIndex >= 0 && currentEntity) {
      dataField = `${fields[nextEditableIndex].id}-${currentEntity.id}`;
    } else if (nextEntity && activeFields[1]) {
      dataField = `${activeFields[1].id}-${nextEntity.id}`;
    }

    if (dataField) {
      const el = document.querySelector(`[data-field=${dataField}]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "end" });
      }
    }
  };

  const handlePrevSelectedCell = () => {
    fillDownReset();
    prevSelectedCell(fields, selectedCoords[0], selectedCoords[1]);
    const activeFields = fields.filter(fieldItem => !fieldItem.inactive && fieldItem.visible);
    const currentField = fields[selectedCoords[1]];
    const currentEditableIndex = currentField
      ? activeFields.findIndex(fieldItem => fieldItem.id === currentField.id)
      : -1;
    const prevEditableIndex = currentEditableIndex - 1;

    const currentEntity = entities[selectedCoords[0]];
    const prevEntity = entities[selectedCoords[0] - 1];

    let dataField;
    if (prevEditableIndex >= 1 && currentEntity && activeFields[prevEditableIndex]) {
      dataField = `${activeFields[prevEditableIndex].id}-${currentEntity.id}`;
    } else if (prevEntity && activeFields.length > 0) {
      dataField = `${activeFields[activeFields.length - 1].id}-${prevEntity.id}`;
    }

    if (dataField) {
      const el = document.querySelector(`[data-field=${dataField}]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  };

  const handleNextSelectedRow = () => {
    fillDownReset();
    nextSelectedRow(selectedCoords[0], selectedCoords[1]);
  };

  const handlePrevSelectedRow = () => {
    fillDownReset();
    prevSelectedRow(selectedCoords[0], selectedCoords[1]);
  };

  const navigationEvents = {
    left: handlePrevSelectedCell,
    right: handleNextSelectedCell,
    down: handleNextSelectedRow,
    up: handlePrevSelectedRow,
  };

  const modifyEvents = {
    onCopy: () => selectCopyDiapason(),
    onCut: () => selectCopyDiapason({ cutMode: true }),
    onPaste: handlePaste,
  };

  const handleCleanSelect = () => {
    setCopyDiapason([]);

    if (isNotEditState) {
      fillDownReset();
      setSelectedCoords([]);
    }
  };

  const hasValidSelection = !isEmpty(selectedCoords);
  const canNavigateCells = isNotEditState && hasValidSelection;

  useOnEscEffect(innerRef, handleCleanSelect);
  useOnReFocuse(innerRef, handleCleanSelect);
  useNavigationEffect(innerRef, canNavigateCells ? navigationEvents : {});
  useOnCopyPasteEffect(innerRef, canNavigateCells ? modifyEvents : {});
  useOnShiftTabEffect(innerRef, canNavigateCells ? handlePrevSelectedCell : null);
  useOnTabEffect(innerRef, canNavigateCells ? handleNextSelectedCell : null);

  const { rowCount, loadMoreRows, isRowLoaded } = useInfinityScroll({
    entities,
    totalCount,
    rowsCount,
    onLoadMore,
    loading,
  });

  const onRowsRendered = useInfiniteLoader({ isRowLoaded, loadMoreRows, rowCount });

  // Keep selection/editing anchored to the same entity when rows are inserted/removed/reordered.
  // This prevents the user from being "moved" to a different row (e.g., when a subscription prepends a new asset).
  useLayoutEffect(() => {
    const prevEntities = prevEntitiesRef.current || [];
    const idToIndex = new Map();
    entities.forEach((entity, index) => {
      if (entity?.id) idToIndex.set(entity.id, index);
    });

    const coordsEqual = (a, b) => a?.[0] === b?.[0] && a?.[1] === b?.[1];

    const mapCoords = coords => {
      if (isEmpty(coords)) return coords;
      const [row, column] = coords;
      const prevId = prevEntities?.[row]?.id;
      if (!prevId) return coords;

      const nextRow = idToIndex.get(prevId);
      if (nextRow === undefined) return null;
      return [nextRow, column];
    };

    const nextSelected = mapCoords(selectedCoords);
    if (nextSelected === null) {
      setSelectedCoords([]);
    } else if (!isEmpty(nextSelected) && !coordsEqual(nextSelected, selectedCoords)) {
      setSelectedCoords(nextSelected);
    }

    const nextEdit = mapCoords(editCoords);
    if (nextEdit === null) {
      setEditCoords([]);
    } else if (!isEmpty(nextEdit) && !coordsEqual(nextEdit, editCoords)) {
      setEditCoords(nextEdit);
    }

    const nextEnd = mapCoords(endCoords);
    if (nextEnd === null) {
      setEndCoords([]);
    } else if (!isEmpty(nextEnd) && !coordsEqual(nextEnd, endCoords)) {
      setEndCoords(nextEnd);
    }

    if (!isEmpty(copyDiapason)) {
      const nextStart = mapCoords(copyDiapason?.[0]);
      const nextFinish = mapCoords(copyDiapason?.[1]);

      if (nextStart === null || nextFinish === null) {
        setCopyDiapason([]);
      } else if (
        !coordsEqual(nextStart, copyDiapason?.[0]) ||
        !coordsEqual(nextFinish, copyDiapason?.[1])
      ) {
        setCopyDiapason([nextStart, nextFinish]);
      }
    }

    prevEntitiesRef.current = entities;
  }, [entities]);

  const getSelectedEntities = rows => {
    return rows.map(index => entities[index]);
  };

  const selectionSummary = useMemo(() => {
    if (isEmpty(rootCoords)) return null;

    const finalCoords = isEmpty(endCoords) ? rootCoords : endCoords;
    const [startRow, startColumn] = rootCoords;
    const [endRow, endColumn] = finalCoords;
    const selectedFields = fields.slice(
      Math.min(startColumn, endColumn),
      Math.max(startColumn, endColumn) + 1
    );
    const summableFields = selectedFields.filter(field => field.summable);

    if (summableFields.length === 0) return null;

    const values = entities
      .slice(Math.min(startRow, endRow), Math.max(startRow, endRow) + 1)
      .flatMap(entity =>
        summableFields
          .map(field => callerField(entity, field))
          .filter(value => value !== "" && value !== null && value !== undefined)
          .map(Number)
          .filter(Number.isFinite)
      );

    if (values.length === 0) return null;

    return {
      count: values.length,
      sum: values.reduce((total, value) => total + value, 0),
    };
  }, [endCoords, entities, fields, rootCoords]);

  const tableRowProps = {
    size,
    entities,
    onChange: onChangeEntity,
    fields,
    fieldsProps,
    fieldsWidth,

    fillDownElementProps,
    fillDownCellProps,

    setEndCoords,
    setEditCoords,
    setSelectedCoords,

    nextCell: nextSelectedCell,
    prevCell: prevSelectedCell,
    nextRow: nextSelectedRow,
    prevRow: prevSelectedRow,

    fillDownMode,
    editCoords,
    rootCoords,
    endCoords,
    copyDiapason,
    getSelectedEntities: getSelectedEntities,
    getRowBackgroundColor,
  };

  useEffect(() => {
    if (fillDownMode) {
      setNoselect(true);
    } else {
      setNoselect(false);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }
  }, [fillDownMode]);

  return (
    <div className={clsx({ noselect })} ref={innerRef}>
      <div style={{ minHeight: 4 }}>{loading && <LinearProgress />}</div>
      <List
        className={clsx({ "no-scrollbars": disabledScroll })}
        onRowsRendered={onRowsRendered}
        style={{ height, width: "100%" }}
        rowHeight={rowHeight}
        rowProps={tableRowProps}
        rowCount={rowCount}
        rowComponent={TableRowMemo}
      >
        {tableHeader}
      </List>
      {selectionSummary && (
        <Box
          data-testid="table-selection-summary"
          display="flex"
          gap={2}
          px={2}
          py={1}
          borderTop={1}
          borderColor="divider"
          justifyContent="flex-end"
        >
          <Typography variant="body2">Count: {selectionSummary.count}</Typography>
          <Typography variant="body2" fontWeight={600}>
            Sum: {selectionSummary.sum.toLocaleString()}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default TableBody;
